import React, { useState, useEffect, useRef, useCallback } from 'react'
import '../../styles/content/ContentList.css'
import '../../styles/CreateOwnContent.css'
import { deleteById, getTemplates } from '../../server/server'
import { useUser } from '../../auth'
import ConfirmationModal from '../modals/ConfirmationModal'
import { format_day } from '../../utils/format_day'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { ReactComponent as DeleteContent } from '../../assets/svg/DeleteContent.svg'
import { ReactComponent as EditContent } from '../../assets/svg/EditContent.svg'
import { notify, prefersReducedMotion, translateTargetText } from '../../utils/util'
import InfiniteScroll from 'react-infinite-scroll-component'
import Loading from '../Loading'

// Cards past this index share the same entrance delay (same cap as the classroom).
const STAGGER_CAP = 12
const SKELETON_CARDS = 4
const HIGHLIGHT_MS = 1400

/**
 * Stamp each item with its position inside the page it arrived in. The stagger
 * reads this instead of the list index so appended pages start from zero and
 * cards already on screen never change their animation delay.
 */
const withEnterIndex = (list = []) => list.map((item, i) => ({ ...item, _enter: Math.min(i, STAGGER_CAP) }))

function ContentTemplate({ setSelTemplate, type, selectTemplateToEdit = () => {}, focusTemplateId = null }) {
    const { user } = useUser()
    const { t } = useTranslation()

    const [contents, setContent] = useState([])
    const [deleteModalOpen, setDelModalOpen] = useState(false)
    const [contentID, setContentID] = useState('')
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [resultNum, setResultNum] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [highlightId, setHighlightId] = useState(null)
    const listRef = useRef(null)
    const mountedRef = useRef(true)
    const focusHandledRef = useRef(false)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    const loadContent = async (loaded = 0) => {
        const firstPage = loaded === 0
        if (firstPage) setLoading(true)
        else setLoadingMore(true)
        try {
            const resp = await getTemplates({ type, user_id: user.id, loaded })
            if (!mountedRef.current) return
            if (resp?.status === 200) {
                const total = resp?.data?.size ?? 0
                const page = withEnterIndex(resp?.data?.list ?? [])
                // `loadMore` refuses to start a second request while one is in
                // flight, so the closed-over `contents` is current here.
                const next = firstPage ? page : [...contents, ...page]
                setContent(next)
                setHasMore(total > next.length)
            }
        } finally {
            if (mountedRef.current) {
                setLoading(false)
                setLoadingMore(false)
            }
        }
    }

    useEffect(() => {
        loadContent(resultNum)
    }, [resultNum])

    // Scroll the just-updated template into view once the first page is in.
    // Replaces a fixed 100ms timeout that raced the fetch and usually lost.
    useEffect(() => {
        if (!focusTemplateId || loading || focusHandledRef.current) return
        const card = listRef.current?.querySelector(`[data-template-id="${focusTemplateId}"]`)
        if (!card) return
        focusHandledRef.current = true
        card.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' })
        setHighlightId(focusTemplateId)
    }, [focusTemplateId, loading, contents])

    // Separate from the scroll effect so a page append cannot cancel the timer.
    useEffect(() => {
        if (!highlightId) return
        const timer = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS)
        return () => clearTimeout(timer)
    }, [highlightId])

    const closeDelModal = () => {
        setDelModalOpen(false)
    }
    const openDelModal = useCallback((id) => {
        setDelModalOpen(true)
        setContentID(id)
    }, [])
    const deleteContent = async () => {
        const resp = await deleteById({ collection: 'template', id: contentID })
        if (!mountedRef.current) return
        setDelModalOpen(false)
        if (resp?.status === 200) {
            notify(t('templateDeleted'))
            setContent((prev) => prev.filter((c) => c.id !== contentID))
        } else {
            notify(t('templateIsNotDeleted'), 'error')
        }
    }

    const loadMore = () => {
        if (!loading && !loadingMore) setResultNum(contents.length)
    }

    const showSkeleton = loading && contents.length === 0

    return (
        <div className="w-full flex flex-wrap gap-4 mt-5" ref={listRef}>
            {showSkeleton ? (
                <TemplateSkeleton />
            ) : (
                <InfiniteScroll
                    dataLength={contents.length}
                    next={loadMore}
                    hasMore={hasMore}
                    scrollableTarget="scroll-inf-target"
                    style={{ overflowX: 'hidden', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 20 }}
                    scrollThreshold={0.8}
                    loader={
                        <div className="col-span-2 flex justify-center py-4" aria-hidden="true">
                            <Loading />
                        </div>
                    }
                >
                    {contents.map((content) => (
                        <Template
                            key={content.id}
                            content={content}
                            openDelModal={openDelModal}
                            setSelTemplate={setSelTemplate}
                            editTemplate={selectTemplateToEdit}
                            highlighted={highlightId === content.id}
                        />
                    ))}
                    {!loading && contents.length === 0 && (
                        <p className="coc-fade-in col-span-2 text-center text-deepPurple text-base font-semibold w-full mt-20">{t('No items found')}</p>
                    )}
                </InfiniteScroll>
            )}

            <ConfirmationModal modalIsOpen={deleteModalOpen} closeModal={closeDelModal} confirmation={deleteContent} message={t('doYouWantToDeleteThis')} />
        </div>
    )
}

const Template = React.memo(function Template({ content, openDelModal = () => {}, setSelTemplate = () => {}, editTemplate = () => {}, highlighted = false }) {
    const { t } = useTranslation()
    const [showMore, setShowMore] = useState(false)
    const rtlStyle = i18n.language === 'ar' ? { right: 'auto', left: '20px' } : undefined
    const message = content?.message ?? ''

    return (
        <div
            className={`coc-card w-[325px] p-4 bg-[#F8F8FB] rounded-xl flex-[1_1_325px] flex flex-col ${highlighted ? 'coc-card-highlight' : ''}`}
            id={content.id}
            data-template-id={content.id}
            style={{ '--i': content._enter ?? 0 }}
        >
            <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between">
                    <h3 className="text-deepPurple font-bold text-xl text-left flex-1">{content?.title}</h3>
                    <button
                        type="button"
                        className="coc-icon-button pointer mr-[10px]"
                        onClick={() => editTemplate(content)}
                        style={rtlStyle}
                        id="edit-template-button"
                        aria-label={t('updateTemplate')}
                    >
                        <EditContent fill="#BEBECE" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="coc-icon-button pointer"
                        id="delete-template-button"
                        onClick={() => openDelModal(content?.id)}
                        style={rtlStyle}
                        aria-label={t('doYouWantToDeleteThis')}
                    >
                        <DeleteContent fill="#FF747F" aria-hidden="true" />
                    </button>
                </div>

                <p className="text-[#413F5E] leading-[22px] text-sm text-left mt-3 text-[16px]">
                    {showMore ? message : message.substring(0, 160)}
                    {!showMore && message.length > 160 && '...'}
                    {message.length > 85 && (
                        <button
                            type="button"
                            className="coc-link font-bold text-[#5E639B] pointer text-[14px] block"
                            onClick={() => setShowMore((prev) => !prev)}
                            aria-expanded={showMore}
                        >
                            {!showMore ? t('showMore') : t('showLess')}
                        </button>
                    )}
                </p>

                <div className="flex flex-col flex-1 justify-end">
                    <div className="flex justify-between items-center my-2">
                        <span className="text-[#5E639B] font-semibold text-base">{t('targetGroup')} </span>
                        <div className="text-[#5E639B] font-semibold text-base flex items-center justify-center bg-white rounded-full py-2 min-w-[130px]">
                            {translateTargetText(content?.target_text) ?? t('Select target')}
                        </div>
                    </div>
                    <div className="flex justify-between items-center my-2">
                        <span className="text-[#5E639B] font-semibold text-base">{t('schedule')} </span>
                        <div className="text-[#5E639B] font-semibold flex items-center justify-center text-base bg-white rounded-full py-2 min-w-[130px]">
                            {content?.schedule ? format_day(content?.publicationDate, true, i18n.language) : t('off')}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-center items-center w-full">
                <button
                    type="button"
                    className="coc-button coc-button-primary rounded-full py-3 w-[100px] flex items-center justify-center font-semibold pointer"
                    onClick={() => setSelTemplate(content)}
                    id={`select-template-button-${content.id}`}
                >
                    {t('select')}
                </button>
            </div>
        </div>
    )
})

/** First-load placeholder shaped like the real cards, so the grid doesn't jump. */
const TemplateSkeleton = () => (
    <div className="w-full" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 20 }} aria-hidden="true">
        {Array.from({ length: SKELETON_CARDS }, (_, i) => (
            <div key={i} className="coc-card coc-skeleton-card w-[325px] p-4 rounded-xl flex-[1_1_325px] flex flex-col" style={{ '--i': i }}>
                <div className="coc-skeleton-block coc-skeleton-title" />
                <div className="coc-skeleton-block coc-skeleton-line" />
                <div className="coc-skeleton-block coc-skeleton-line" style={{ width: '70%' }} />
                <div className="flex justify-between items-center my-2 mt-6">
                    <div className="coc-skeleton-block coc-skeleton-label" />
                    <div className="coc-skeleton-block coc-skeleton-pill" />
                </div>
                <div className="flex justify-between items-center my-2">
                    <div className="coc-skeleton-block coc-skeleton-label" />
                    <div className="coc-skeleton-block coc-skeleton-pill" />
                </div>
                <div className="flex justify-center mt-3">
                    <div className="coc-skeleton-block coc-skeleton-button" />
                </div>
            </div>
        ))}
    </div>
)

export default ContentTemplate
